import * as Sentry from "@sentry/react";
import { DateTime } from "luxon";
import { isExpectedError } from "./utils/isExpectedError";
import safeJsonStringify_ from "safe-json-stringify";
import { safeErrorToString, safeToString } from "./backoffice.utils";
import { auth, firestore } from "./firebase";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import _omit from "lodash/omit";
import { currentVersion } from "../version";

export const errorReportProps: { [key: string]: any } = {};

export function initSentry() {
  // See https://docs.sentry.io/platforms/javascript/guides/react/
  Sentry.init({
    dsn:
      location.hostname === "backoffice.autovio.de"
        ? "https://eec770aec08c455195df6055d04118a8@o956470.ingest.sentry.io/5905853"
        : undefined,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserProfilingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ["info", "warn", "error"] }),
      Sentry.replayIntegration({
        blockAllMedia: false,
        maskAllInputs: false,
        maskAllText: false,
      }),
    ],
    tracesSampleRate: 0.1,
    profilesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    release: currentVersion,
    environment: location.hostname === "backoffice.autovio.de" ? "prod" : "dev",
    beforeBreadcrumb: (breadcrumb, hint) => {
      const { category, data } = breadcrumb;
      const url = typeof data?.url === "string" ? data.url : "";
      if (category === "fetch" && url.startsWith("https://firestore.")) {
        return null;
      }
      if (breadcrumb.category === "ui.click") {
        const srcElement = hint?.event?.srcElement;
        if (srcElement) {
          breadcrumb = { ...breadcrumb, message: `Element: ${elementToString(srcElement)}` };
        }
      }
      addBreadcrumbToLogBuffer(breadcrumb, hint);
      return breadcrumb;
    },
    beforeSend: (event, hint) => {
      // Only report unexpected errors to sentry ...
      if (event.level !== "fatal" && event.level !== "error") {
        return null;
      }
      const { message } = event;
      if (message?.startsWith("Warning:") || isExpectedError(event, hint)) {
        return null;
      }
      void saveErrorInFirestore(event, hint);
      return event;
    },
  });
}

const _logBuffer: Array<string> = [];
const _logBufferMaxSize = 100;
let _logBufferIndex = 0;

function addBreadcrumbToLogBuffer(breadcrumb: Sentry.Breadcrumb, hint?: Sentry.BreadcrumbHint): void {
  const { category, data, level, message, timestamp } = breadcrumb;
  if (category === "sentry.transaction") {
    return;
  }
  const t = DateTime.fromMillis(timestamp ? timestamp * 1000 : Date.now(), { zone: "Europe/Berlin" });
  let line = t.toFormat("HH:mm:ss.SSS");
  if (level === "fatal" || level === "error") {
    line += " ERROR:";
  } else if (level === "warning") {
    line += " WARNING:";
  }
  if (category === "console") {
    line += " " + message;
  } else if (category === "ui.click" && message) {
    line += "[ui.click] " + message;
  } else {
    line += " [" + category + "]";
    if (data) {
      if (category === "fetch" || category === "xhr") {
        const { method, url, status_code } = data;
        line += ` ${method} ${url} => ${status_code}`;
      } else if (category === "navigation") {
        const { from, to } = data;
        line += ` ${from} => ${to}`;
      } else {
        line += " " + safeJsonStringify({ breadcrumb, hint });
      }
    } else {
      line += " " + safeJsonStringify({ breadcrumb, hint });
    }
  }
  if (_logBuffer.length < _logBufferMaxSize) {
    _logBuffer.push(line);
  } else {
    _logBuffer[_logBufferIndex] = line;
    _logBufferIndex = (_logBufferIndex + 1) % _logBufferMaxSize;
  }
}

function getLog(): Array<string> {
  if (_logBufferIndex === 0) {
    return _logBuffer;
  }
  return _logBuffer.slice(_logBufferIndex).concat(_logBuffer.slice(0, _logBufferIndex));
}

function safeJsonStringify(x: any, replacer?: null | ((key: any, value: any) => any), space?: string | number): string {
  try {
    return safeJsonStringify_(x, replacer, space);
  } catch (error) {
    return safeErrorToString(error);
  }
}

function elementToString(elm: any): string {
  const { innerText, localName, ariaLabel, placeholder } = elm;
  let result = localName;
  if (innerText) {
    result += ` | innerText: ${innerText}`;
  }
  if (ariaLabel) {
    result += ` | ariaLabel: ${ariaLabel}`;
  }
  if (placeholder) {
    result += ` | placeholder: ${placeholder}`;
  }
  return result;
}

let _lastErrorDateTime: undefined | DateTime;

async function saveErrorInFirestore(event: Sentry.Event, hint?: Sentry.EventHint): Promise<void> {
  try {
    if (!location.hostname.startsWith("backoffice.autovio.")) {
      return;
    }
    const { currentUser } = auth;
    if (!currentUser) {
      return;
    }
    const { timestamp } = event;
    const t = DateTime.fromMillis(timestamp ? timestamp * 1000 : Date.now(), { zone: "Europe/Berlin" });
    // Rate limit: do not save more than 1 error per second ...
    if (_lastErrorDateTime && t.diff(_lastErrorDateTime).milliseconds < 1000) {
      return;
    }
    _lastErrorDateTime = t;
    const errorMessage = getErrorMessage(event);
    const documentId = sanitizeFirebaseDocumentId(t.toFormat("yyyy-MM-dd HH:mm:ss") + " " + errorMessage);
    const errorData = Object.fromEntries(
      Object.entries({
        source: "backoffice",
        backofficeVersion: currentVersion,
        ...errorReportProps,
        currentUrl: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: Timestamp.fromMillis(t.valueOf()),
        event: safeJsonStringify(_omit(event, ["breadcrumbs"])),
        ...(hint ? { hint: safeJsonStringify(hint) } : {}),
        log: getLog(),
        error: errorMessage,
        sentryUrl: `https://autovio.sentry.io/autovio/backoffice/events/${event.event_id}`,
      }).filter(([_, value]) => value),
    );
    await setDoc(doc(firestore, `users/${currentUser.uid}/errors/${documentId}`), errorData);
  } catch (error) {
    console.error("Failed to save error in Firestore", error);
  }
}

function getErrorMessage(event: Sentry.Event): string {
  const { exception, extra, message } = event;
  if (message) {
    return message;
  }
  const e = exception?.values?.[0];
  if (e?.mechanism?.type === "console" && extra) {
    const a = extra.arguments;
    if (Array.isArray(a) && a.length > 0) {
      return a
        .map((it) => {
          if (typeof it === "string") {
            return it;
          } else if (it.message) {
            return it.message;
          } else {
            return safeToString(it);
          }
        })
        .join(" ");
    }
  }
  return e?.value ?? "Unknown error";
}

/** See "Constraints on document IDs" on https://firebase.google.com/docs/firestore/quotas#limits */
function sanitizeFirebaseDocumentId(s: string): string {
  // Cannot contain a forward slash (/)
  s = s.replaceAll("/", "_");
  // Cannot solely consist of a single period (.) or double periods (..)
  if (s === ".") {
    s = "_";
  } else if (s === "..") {
    s = "__";
  }
  // Cannot match the regular expression __.*__
  if (s.length >= 4 && s.startsWith("__") && s.endsWith("__")) {
    let i = s.length - 2;
    while (i > 2 && s[i - 1] === "_") {
      --i;
    }
    s = s.substring(0, i);
  }
  // Must be no longer than 1,500 bytes
  if (s.length > 300) {
    s = s.substring(0, 300);
  }
  return s;
}
